// â”€â”€â”€ Merge contacts modal (Phase 3) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
//
// Soft-merge wizard. Admin picks survivor + merged (the latter goes inactive
// with mergedIntoContactId pointer). Survivor inherits non-empty fields the
// merged has that survivor doesn't.

import React, { useState } from 'react';
import type { Contact } from '../types';
import { mergeContacts } from '../services/contactsService';
import { isSyntheticEmail } from '../lib/email';

interface Props {
  open: boolean;
  primary: Contact | null;            // the "current" contact (likely survivor)
  candidates: Contact[];              // other active contacts for picker
  onClose: () => void;
  onMerged: () => void;
}

const ContactCard: React.FC<{ c: Contact; selected?: boolean; onClick?: () => void }> = ({
  c, selected, onClick,
}) => (
  <div
    onClick={onClick}
    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
      selected
        ? 'border-[#011132] bg-[#011132]/5'
        : 'border-stone-200 hover:border-stone-400'
    }`}
  >
    <div className="flex items-center gap-2">
      <div className="w-8 h-8 rounded-full bg-[#011132]/10 flex items-center justify-center text-[#011132] text-xs font-bold">
        {c.displayName?.[0]?.toUpperCase() ?? '?'}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-stone-800 truncate">{c.displayName}</p>
        <p className="text-xs text-stone-500 truncate">
          {isSyntheticEmail(c.email) ? <em>synthetic</em> : c.email}
        </p>
      </div>
      {selected && (
        <span className="material-symbols-rounded text-[#011132]" style={{ fontSize: 18 }}>
          check_circle
        </span>
      )}
    </div>
    <div className="text-[10px] text-stone-400 mt-1 grid grid-cols-2 gap-x-2">
      <span>Dept: {c.department || 'â€”'}</span>
      <span>Source: {c.source}</span>
      {c.hasAppAccount && <span className="text-blue-600 col-span-2">âš  Has app account</span>}
    </div>
  </div>
);

const MergeContactsModal: React.FC<Props> = ({ open, primary, candidates, onClose, onMerged }) => {
  const [mergedId, setMergedId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  if (!open || !primary) return null;

  const otherContacts = candidates.filter(
    (c) => c.id !== primary.id && c.status === 'active',
  );
  const q = search.toLowerCase().trim();
  const filtered = q
    ? otherContacts.filter(
        (c) =>
          c.displayName?.toLowerCase().includes(q) ||
          c.email?.toLowerCase().includes(q) ||
          c.phone?.includes(q),
      )
    : otherContacts.slice(0, 50);

  const mergedContact = mergedId ? otherContacts.find((c) => c.id === mergedId) : null;

  async function handleMerge() {
    if (!mergedId || !primary) return;
    setSubmitting(true);
    setError(null);
    try {
      await mergeContacts(primary.id, mergedId, 'Manual merge via admin UI');
      onMerged();
      onClose();
      setMergedId(null);
      setSearch('');
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto pointer-events-none">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl pointer-events-auto my-8"
             style={{ fontFamily: 'Inter, sans-serif' }}>
          {/* Header */}
          <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-stone-900">Merge Duplicates</h2>
              <p className="text-xs text-stone-500 mt-0.5">
                The MERGED contact becomes inactive with a pointer to the SURVIVOR.
              </p>
            </div>
            <button onClick={onClose} className="text-stone-400 hover:text-stone-700">
              <span className="material-symbols-rounded" style={{ fontSize: 20 }}>close</span>
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-5">
            {/* Survivor */}
            <div>
              <p className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">
                Survivor (keeps existing references)
              </p>
              <ContactCard c={primary} selected />
            </div>

            {/* Merge picker */}
            <div>
              <p className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">
                Merge INTO survivor (this will become inactive)
              </p>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email, phoneâ€¦"
                className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm mb-2"
              />
              <div className="grid grid-cols-1 gap-2 max-h-72 overflow-y-auto">
                {filtered.length === 0 ? (
                  <p className="text-xs text-stone-400 p-4 text-center">
                    {q ? 'No matches' : 'No other contacts to merge'}
                  </p>
                ) : (
                  filtered.map((c) => (
                    <ContactCard
                      key={c.id}
                      c={c}
                      selected={mergedId === c.id}
                      onClick={() => setMergedId(c.id === mergedId ? null : c.id)}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Preview */}
            {mergedContact && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs space-y-1">
                <p className="font-semibold text-amber-900">
                  Preview: merge "{mergedContact.displayName}" into "{primary.displayName}"
                </p>
                <p className="text-amber-800">
                  â€¢ "{mergedContact.displayName}" status â†’ inactive
                </p>
                <p className="text-amber-800">
                  â€¢ mergedIntoContactId â†’ {primary.id}
                </p>
                <p className="text-amber-800">
                  â€¢ Non-empty fields from "{mergedContact.displayName}" copied into "{primary.displayName}" where empty
                </p>
                {primary.hasAppAccount && mergedContact.hasAppAccount && (
                  <p className="text-red-700 font-semibold pt-1 border-t border-amber-200">
                    âš  Both have app accounts â€” merge will fail. Demote one user first.
                  </p>
                )}
              </div>
            )}

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-stone-100 flex items-center justify-end gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 rounded-lg border border-stone-200"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              onClick={handleMerge}
              disabled={!mergedId || submitting || (primary.hasAppAccount && mergedContact?.hasAppAccount)}
              className="px-5 py-2 text-sm font-semibold text-white rounded-lg disabled:opacity-50"
              style={{ background: '#011132' }}
            >
              {submitting ? 'Mergingâ€¦' : 'Merge'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default MergeContactsModal;
